import { createFileRoute } from "@tanstack/react-router"
import { Typography } from "@/components/ui/typography"

export const Route = createFileRoute("/_public/data-deletion-agreement")({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <main>
        <article>
          <header>
            <Typography as="h1" size="3xl" className="mb-4">
              Data Deletion Agreement
            </Typography>
            <Typography as="p" size="sm" className="text-muted-foreground mb-6">
              Effective Date: <time dateTime="2026-06-05">June 5, 2026</time>
            </Typography>
          </header>

          <Typography as="p" className="mb-8">
            This Data Deletion Agreement ("Agreement") describes your rights and responsibilities
            regarding the deletion of your data from Legacy Keeper, operated by Traction
            Technologies Inc. in the U.S. and 1546045 B.C. LTD. in Canada ("Legacy Keeper," "we,"
            "us," or "our"). Please read this Agreement carefully before deleting any data.
          </Typography>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              1. Your Right to Delete Data
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              1.1 Your Deletion Right
            </Typography>
            <Typography as="p" className="mb-6">
              You have the right to delete all of your account data from Legacy Keeper at any time,
              for any reason, without needing to provide justification. This right applies to all
              users, including both Standard Users and Professional Users.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              1.2 Self-Service Deletion
            </Typography>
            <Typography as="p" className="mb-6">
              You can delete your data directly from your account settings page without contacting
              customer support. The deletion process is designed to be simple and under your
              control.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              1.3 No Fees or Penalties
            </Typography>
            <Typography as="p" className="mb-6">
              There are no fees, charges, or penalties for deleting your data. If you have an active
              paid subscription, deleting your data does not entitle you to a refund for any
              remaining subscription period, but you will not be charged again after the current
              billing period ends.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              2. What Gets Deleted
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              2.1 Complete Data Deletion
            </Typography>
            <Typography as="p" className="mb-4">
              When you delete your account data, the following information is permanently removed:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                <strong>All estate planning information:</strong> Personal details, family
                information, asset details, beneficiary information, healthcare directives, and all
                other estate planning data.
              </li>
              <li>
                <strong>All uploaded documents and files:</strong> Any documents, images, PDFs, or
                other files you have uploaded.
              </li>
              <li>
                <strong>All AI-generated content:</strong> Documents, recommendations, and other
                content created by the AI system.
              </li>
              <li>
                <strong>All collaboration data:</strong> Records of people you've shared data with
                and permissions you've granted.
              </li>
              <li>
                <strong>Account information:</strong> Your name, email address, and other account
                details.
              </li>
            </ul>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              2.2 Professional User Specific Deletions
            </Typography>
            <Typography as="p" className="mb-6">
              For Professional Users, deletion also includes professional credentials and licensing
              information, client collaboration records and invitations, and all access permissions
              to client accounts.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              2.3 What Is Not Deleted
            </Typography>
            <Typography as="p" className="mb-4">
              The following information may be retained as legally required:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                <strong>Payment records:</strong> Transaction history for tax and accounting
                compliance (retained for 7 years as required by law).
              </li>
              <li>
                <strong>Aggregated analytics:</strong> Anonymous, non-identifiable usage statistics
                that cannot be traced back to you.
              </li>
              <li>
                <strong>Legal hold data:</strong> Information subject to legal preservation
                requirements.
              </li>
            </ul>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              3. How Data Deletion Works
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              3.1 Deletion Process
            </Typography>
            <Typography as="p" className="mb-4">
              To delete your data:
            </Typography>
            <ol className="list-decimal pl-6 space-y-2 mb-6">
              <li>Log in to your Legacy Keeper account.</li>
              <li>Navigate to Account Settings.</li>
              <li>Select "Delete Account."</li>
              <li>Review the deletion warnings and information.</li>
              <li>
                Export any data you wish to keep (Will need to request a copy of data from
                admin@legacykeeper.app).
              </li>
              <li>Confirm your intention to permanently delete your data.</li>
              <li>Verify your identity (may require password re-entry).</li>
              <li>Click the final "Delete All Data" button.</li>
            </ol>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              3.2 How and When Deletion Happens
            </Typography>
            <Typography as="p" className="mb-6">
              When you confirm deletion, your data is removed from our active, production systems
              promptly. Because we maintain encrypted backups on a rolling 30-day cycle to protect
              all users against data loss, residual copies of your data may remain in our backup
              systems until those backups naturally cycle out. All of your deleted data is fully
              purged from our backup systems within 30 days of your confirmation. After this 30-day
              period, no copies of your deleted data remain on Legacy Keeper's systems.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              3.3 Confirmation Email
            </Typography>
            <Typography as="p" className="mb-6">
              After you confirm deletion, you will receive a confirmation email at your registered
              email address. This email serves as documentation that your deletion has been
              initiated and that your data will be fully purged within 30 days.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              4. Important Warnings About Data Deletion
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4.1 Deletion Is Permanent
            </Typography>
            <Typography as="p" className="mb-6">
              Once your deletion is complete, your data cannot be recovered. We strongly recommend
              exporting any information you may need before you delete.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4.2 Backups and the 30-Day Window
            </Typography>
            <Typography as="p" className="mb-6">
              We retain encrypted backups on a rolling 30-day cycle so that we can recover the
              Service in the event of data loss, accidental deletion, or a system failure. Your
              deleted data is removed from active systems immediately and is fully purged from all
              backup systems within 30 days. We do not retain copies of your data beyond this
              window.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4.3 Export Your Data First
            </Typography>
            <Typography as="p" className="mb-6">
              Before deleting your data, we strongly urge you to export all information you may need
              in the future.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4.4 Impact on Shared Access
            </Typography>
            <Typography as="p" className="mb-4">
              If you have shared your data with others (professional advisors, family members),
              deleting your data immediately revokes their access through Legacy Keeper, terminates
              all collaboration permissions, and means no copies remain on our platform.
            </Typography>
            <Typography as="p" className="mb-6">
              For Professional Users: If you are a Professional User with access to client data,
              deleting your account removes your access to all client accounts. However, it does NOT
              delete your clients' data — their information remains in their own accounts.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4.5 Critical: Data Already Copied by Collaborators Cannot Be Retracted
            </Typography>
            <Typography as="p" className="mb-4">
              If you have shared your data with professional advisors, family members, or other
              users, they may have already downloaded, exported, or copied your information.
              Revoking their access or deleting your account only removes their ability to access
              your data through Legacy Keeper — it does NOT delete any copies they have already
              made.
            </Typography>
            <Typography as="p" className="mb-4">
              When you share data with a Professional User and they use it off the Legacy Keeper
              system:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>They are independently responsible for that off-platform data.</strong> Once
                data leaves our platform, the Professional User must store, secure, and handle it in
                accordance with their own legal, regulatory, fiduciary, and professional obligations
                (which may include the Gramm-Leach-Bliley Act, SEC Regulation S-P, applicable
                Canadian privacy law, and professional conduct standards).
              </li>
              <li>
                <strong>Their copies remain with them:</strong> Any data they have already saved,
                printed, or copied remains in their possession even after you revoke access or
                delete your account.
              </li>
              <li>
                <strong>You cannot retract downloaded data:</strong> Once someone has made a copy of
                your data, you cannot force them to delete it.
              </li>
              <li>
                <strong>Professional obligations may apply:</strong> Professional advisors may be
                required by law or professional standards to retain copies of client information
                even after your collaboration ends.
              </li>
              <li>
                <strong>Legacy Keeper cannot control external copies:</strong> We have no ability to
                delete, retract, or control data that others have downloaded or copied from the
                platform.
              </li>
              <li>
                <strong>Partner service connections:</strong> If you or a Professional User have
                connected an approved partner service (such as a CRM or planning tools) to Legacy
                Keeper through our API, your data may have been shared with that service. Revoking
                the connection or deleting your account stops future access, but it does not retract
                data already sent to the partner service. If you connected the service yourself, you
                are responsible for it and can revoke it directly from your dashboard; if a
                Professional User connected it, you can ask them to revoke its access at any time.
              </li>
            </ul>
            <Typography as="p" className="mb-6">
              If you are concerned about who has copies of your data, you should contact those
              individuals directly. This is particularly important if you have worked with
              professional advisors who may have legal or ethical obligations regarding client data
              retention.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4.6 You Are Responsible for Your Own Backups
            </Typography>
            <Typography as="p" className="mb-6">
              You are solely responsible for maintaining your own backups of any information you
              want to preserve. Our data export service is provided to help you do this, but it is
              your responsibility to export and save your data before deletion.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              5. Special Circumstances
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.1 Active Subscriptions
            </Typography>
            <Typography as="p" className="mb-4">
              If you have an active paid subscription when you delete your data, your subscription
              will be cancelled immediately, you will not be charged for future billing periods, you
              are not entitled to a refund for the current billing period, and any unused
              subscription time is forfeited.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.2 Legal Obligations and Court Orders
            </Typography>
            <Typography as="p" className="mb-4">
              In rare circumstances, Legacy Keeper may be legally prohibited from deleting certain
              data — for example, where it is subject to a legal hold or preservation order, a court
              has ordered retention, we are required by law to retain certain records, or the data
              is needed for ongoing litigation or a regulatory investigation. If any of these apply,
              we will notify you, explain what must be retained and why, retain only the minimum
              necessary, and delete it as soon as the legal obligation ends.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.3 Professional Users with Active Client Relationships
            </Typography>
            <Typography as="p" className="mb-6">
              If you are a Professional User with active access to client accounts, deleting your
              account immediately terminates all client access permissions, your clients' data
              remains intact in their own accounts, and you should inform your clients before
              deleting your account to avoid service disruption.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.4 Deceased User Accounts
            </Typography>
            <Typography as="p" className="mb-6">
              If a Legacy Keeper user passes away, authorized representatives (such as executors or
              next of kin) may request account access or data deletion by contacting us at{" "}
              <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                general@legacykeeper.app
              </a>{" "}
              with appropriate legal documentation. We will require proof of death and legal
              authority before taking any action.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              6. Alternatives to Full Deletion
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              6.1 Partial Data Deletion
            </Typography>
            <Typography as="p" className="mb-6">
              If you don't want to delete all of your data, you can selectively delete specific
              information from within your account, such as individual documents or sections of your
              estate plan, without deleting your entire account.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              6.2 Pausing Use Without Deleting
            </Typography>
            <Typography as="p" className="mb-6">
              If you wish to stop using the Service without deleting your data, you can simply stop
              using your account. Your data remains protected under our Privacy Policy until you
              choose to delete it.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              6.3 Data Export Without Deletion
            </Typography>
            <Typography as="p" className="mb-6">
              You can request an export of your data at any time without deleting it. This allows
              you to maintain a personal backup while continuing to use Legacy Keeper. We recommend
              regularly exporting your data as a best practice.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              6.4 Revoke Access Permissions
            </Typography>
            <Typography as="p" className="mb-6">
              If you're concerned about who has access to your data, you can revoke access
              permissions for specific users without deleting your account or data. If you have
              connected a partner service to your account through our API, you can revoke that
              connection directly from your dashboard at any time. If a Professional User has
              connected a partner service to your data, you can request that they revoke that
              connection, and revoking the Professional User's access ends any partner access
              derived from it.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              7. Your Acknowledgment and Agreement
            </Typography>
            <Typography as="p" className="mb-4">
              By using Legacy Keeper's data deletion features, you acknowledge and agree that:
            </Typography>
            <ol className="list-decimal pl-6 space-y-2 mb-6">
              <li>You have read and understood this Data Deletion Agreement.</li>
              <li>You understand that, once complete, data deletion is permanent.</li>
              <li>
                You understand that deleted data is fully purged from our systems within 30 days.
              </li>
              <li>
                You are responsible for exporting and backing up any data you want to preserve.
              </li>
              <li>You understand the impact of deletion on shared access and collaboration.</li>
              <li>
                You understand that Legacy Keeper cannot retract copies held by collaborators
                off-platform.
              </li>
            </ol>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              8. Limitation of Liability for Deleted Data
            </Typography>
            <Typography as="p" className="mb-6">
              To the maximum extent permitted by law, Legacy Keeper and Traction Technologies Inc. /
              1546045 B.C. LTD. shall not be liable for any loss or consequences resulting from your
              decision to delete data, including loss of estate planning information, inability to
              recover deleted data, disruption to professional relationships, or any direct,
              indirect, incidental, or consequential damages. You assume the risk associated with
              deleting your data.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              9. Contact and Support
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              9.1 Questions Before Deletion
            </Typography>
            <Typography as="p" className="mb-4">
              If you have questions about data deletion before proceeding, contact us at{" "}
              <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                general@legacykeeper.app
              </a>
              . Our support team can help you understand what data will be deleted, how to export
              your data, and the deletion process and timeline.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              9.2 After Deletion
            </Typography>
            <Typography as="p" className="mb-6">
              Once data has been purged, our support team cannot recover it, as restoration is not
              possible after the 30-day purge window.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              9.3 Contact Information
            </Typography>
            <Typography as="p" className="mb-2 font-semibold">
              Traction Technologies Inc. / 1546045 B.C. LTD.
            </Typography>
            <Typography as="p" className="mb-2">
              Email:{" "}
              <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                general@legacykeeper.app
              </a>
            </Typography>
            <Typography as="p" className="mb-2">
              Website:{" "}
              <a href="https://legacykeeper.app" className="text-primary hover:underline">
                https://legacykeeper.app
              </a>
            </Typography>
            <Typography as="p" className="mb-6">
              Subject Line: Data Deletion Inquiry
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              10. Changes to This Agreement
            </Typography>
            <Typography as="p" className="mb-8">
              We may update this Agreement from time to time. Material changes will be posted with
              an updated effective date and communicated via email at least 30 days before taking
              effect. Your continued use of the deletion features after changes take effect
              constitutes acceptance of the updated Agreement.
            </Typography>
          </section>
        </article>
      </main>
    </div>
  )
}
